/**
 * Viewer-side TRIBUTE / IN MEMORIAM page.
 *
 * Two exports:
 *   • <TributeView tribute onClose /> — the fullscreen respectful
 *     memorial view (hero image, name, dates, obituary, gallery,
 *     linked channel/tape, soft-fade-in audio).
 *   • <TributeBanner tribute onOpen /> — the subtle 24h strip that
 *     appears at the top of the viewport when a new tribute is
 *     published. Dismissable per publish_id via localStorage so a
 *     fresh publish always resurfaces.
 *
 * Aesthetic: hushed. Black, near-black, sepia-warm gradient over the
 * hero, phosphor headings, no marquee scrolling, no bright borders.
 * Escape closes the view. Audio fades in from 0 → 0.35 over ~2s so it
 * never startles.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Heart, X, Volume2, VolumeX, Play, Pause, ExternalLink, Archive, ChevronLeft } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const BANNER_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const DISMISS_PREFIX = "crt_tribute_dismissed_";

function isBannerFresh(tribute) {
  if (!tribute || !tribute.enabled || !tribute.published_at) return false;
  try {
    const t = Date.parse(tribute.published_at);
    if (Number.isNaN(t)) return false;
    return Date.now() - t < BANNER_TTL_MS;
  } catch (e) {
    return false;
  }
}

function isDismissed(tribute) {
  if (!tribute || !tribute.published_at) return false;
  try {
    return localStorage.getItem(DISMISS_PREFIX + tribute.published_at) === "1";
  } catch (e) {
    return false;
  }
}

function markDismissed(tribute) {
  if (!tribute || !tribute.published_at) return;
  try { localStorage.setItem(DISMISS_PREFIX + tribute.published_at, "1"); }
  catch (e) { /* noop */ }
}

// ─── Banner ───────────────────────────────────────────────────────────────

export function TributeBanner({ tribute, onOpen }) {
  // Track dismissal in state so the strip disappears immediately when
  // the viewer taps X — no reload required.
  const [dismissed, setDismissed] = useState(() => isDismissed(tribute));

  useEffect(() => {
    setDismissed(isDismissed(tribute));
  }, [tribute?.published_at]);

  if (!isBannerFresh(tribute)) return null;
  if (dismissed) return null;

  return (
    <div
      className="fixed top-0 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 px-3 py-1.5 bg-black/85 border border-[color-mix(in_srgb,var(--phosphor)_55%,transparent)] shadow-[0_2px_12px_rgba(0,0,0,0.6)] backdrop-blur-sm"
      style={{ fontFamily: "VT323, monospace" }}
      data-testid="tribute-banner"
    >
      <Heart size={12} className="opacity-85" />
      <button
        type="button"
        onClick={onOpen}
        data-testid="tribute-banner-open"
        className="text-[12px] tracking-[0.22em] hover:underline decoration-dotted underline-offset-4"
      >
        <span className="opacity-70">▲ IN MEMORIAM · </span>
        <span className="font-bold">{tribute.name}</span>
        {tribute.death_year ? (
          <span className="opacity-70"> ({tribute.birth_year ? `${tribute.birth_year}–` : ""}{tribute.death_year})</span>
        ) : null}
      </button>
      <button
        type="button"
        onClick={() => { markDismissed(tribute); setDismissed(true); }}
        data-testid="tribute-banner-dismiss"
        title="Dismiss (until the next tribute)"
        className="opacity-55 hover:opacity-100 ml-1"
      >
        <X size={11} />
      </button>
    </div>
  );
}

// ─── Fullscreen tribute page ──────────────────────────────────────────────

export default function TributeView({ tribute, channels, onClose, onTuneChannel, onOpenTape }) {
  const audioRef = useRef(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioMuted, setAudioMuted] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { url, caption }
  // Waveform visualiser — Web Audio AnalyserNode tapping the same
  // <audio> element via a single MediaElementAudioSourceNode. The
  // source can only be created once per element (browser limitation),
  // so we stash it + the AudioContext + Analyser on refs and reuse
  // them across pause/play. Canvas is drawn on rAF only while
  // playing; the last frame stays painted on pause so the strip
  // doesn't blink out.
  const canvasRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const rafRef = useRef(null);
  // Internal view mode — one component handles three surfaces so the
  // ESC / close plumbing stays coherent:
  //   • "current"  — live tribute (auto-fade audio, hero, gallery)
  //   • "list"     — grid of past tributes fetched from /archive
  //   • "archived" — read-only view of a specific archived tribute
  //                  (no auto-fade audio; manual play only)
  const [viewMode, setViewMode] = useState("current");
  const [archives, setArchives] = useState([]); // light metadata list
  const [archived, setArchived] = useState(null); // full doc when viewing one

  // ── Web Audio waveform helpers ─────────────────────────────────────
  // Ref-cached so the MediaElementSource is created exactly once per
  // <audio> element (browsers throw on a second createMediaElementSource
  // call for the same element). rAF draw loop is scheduled by
  // startWaveformLoop, cancelled by stopWaveformLoop.
  const ensureAudioGraph = () => {
    const el = audioRef.current;
    if (!el) return null;
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtxRef.current = new Ctx();
    }
    const ctx = audioCtxRef.current;
    if (!sourceNodeRef.current) {
      try {
        sourceNodeRef.current = ctx.createMediaElementSource(el);
        analyserRef.current = ctx.createAnalyser();
        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.75;
        sourceNodeRef.current.connect(analyserRef.current);
        // Passthrough to destination so audio still plays out the
        // speakers (creating a source pulls the default routing).
        sourceNodeRef.current.connect(ctx.destination);
      } catch (e) {
        // Cross-origin audio (or a re-connection attempt) throws —
        // visualiser silently no-ops but audio still plays via the
        // element's default output.
        return null;
      }
    }
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    return analyserRef.current;
  };
  const startWaveformLoop = () => {
    const analyser = analyserRef.current;
    const canvas = canvasRef.current;
    if (!analyser || !canvas) return;
    const ctx2d = canvas.getContext("2d");
    if (!ctx2d) return;
    const bufLen = analyser.frequencyBinCount;
    const buf = new Uint8Array(bufLen);
    const draw = () => {
      const w = canvas.clientWidth || canvas.width;
      const h = canvas.clientHeight || canvas.height;
      if (!w || !h) { rafRef.current = requestAnimationFrame(draw); return; }
      const dpr = window.devicePixelRatio || 1;
      if (canvas.width !== w * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
      }
      analyser.getByteFrequencyData(buf);
      ctx2d.clearRect(0, 0, canvas.width, canvas.height);
      const barCount = 64;
      const step = Math.floor(bufLen / barCount) || 1;
      const barW = (canvas.width / barCount) * 0.72;
      const gap = (canvas.width / barCount) * 0.28;
      for (let i = 0; i < barCount; i += 1) {
        let sum = 0;
        for (let j = 0; j < step; j += 1) sum += buf[i * step + j] || 0;
        const avg = sum / step / 255; // 0..1
        // Sine-window taper — bars ramp up in the middle, settle at
        // the edges. Matches the hushed page aesthetic.
        const win = Math.sin((i / (barCount - 1)) * Math.PI);
        const amp = Math.max(0.04, avg) * win;
        const barH = Math.max(1 * dpr, amp * canvas.height * 0.9);
        const x = i * (barW + gap);
        const y = (canvas.height - barH) / 2;
        const grey = Math.round(115 + amp * 90); // 115..205
        ctx2d.fillStyle = `rgb(${grey},${grey},${grey})`;
        ctx2d.fillRect(x, y, barW, barH);
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(draw);
  };
  const stopWaveformLoop = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  };
  useEffect(() => () => {
    // Unmount cleanup — cancel any pending frame + close the
    // AudioContext so we don't leak resources when the viewer closes
    // the tribute page.
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
  }, []);

  // Fetch archive metadata on mount so the "◄ PAST TRIBUTES (N)"
  // link can render a real count and the picker paints instantly
  // when the viewer taps it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/site/tribute/archive`);
        if (!cancelled) setArchives(r.data?.items || []);
      } catch (e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // The record actually being displayed — current live tribute OR
  // the archived doc when the viewer clicked one. All render logic
  // below reads from `active` so we don't need to fork the JSX.
  const active = viewMode === "archived" && archived ? archived : tribute;
  const isArchivedView = viewMode === "archived" && !!archived;

  // Effective audio src — prefer the direct-upload stream endpoint if
  // the current/archived tribute has an `audio_stream_url` set,
  // otherwise fall back to the legacy `audio_url` (kept for
  // backwards-compat with tributes published before the direct-upload
  // switch). Passing `active.updated_at` as a cachebust so REPLACE
  // AUDIO from the owner panel refreshes on this page without a reload.
  //
  // Declared HERE (not below the auto-fade effect) so React's synchronous
  // dependency-array evaluation for that effect doesn't hit a Temporal
  // Dead Zone reference — the minified production bundle throws
  // `ReferenceError: Cannot access 'activeAudioSrc' before initialization`
  // when a `const` is referenced in an effect's deps before its
  // declaration line, which manifested as a blank tribute page on
  // scartlead.tv even though the dev bundle rendered fine.
  const activeAudioSrc = active
    ? (active.audio_stream_url
        ? `${active.audio_stream_url.startsWith("http") ? "" : (process.env.REACT_APP_BACKEND_URL || "")}${active.audio_stream_url}?v=${encodeURIComponent(active.updated_at || "")}`
        : (active.audio_url || null))
    : null;

  // Auto-fade the audio in over ~2s when the view mounts. Some browsers
  // block auto-play without a prior user gesture — if the play() promise
  // rejects, we silently give up and expose the manual PLAY button below
  // the audio bar instead. Never blocks the view render. Suppressed for
  // archived tributes — those are dignified read-only reprises, no
  // startle-play theatre.
  useEffect(() => {
    if (isArchivedView) return undefined;
    if (!activeAudioSrc) return undefined;
    const el = audioRef.current;
    if (!el) return undefined;
    el.volume = 0;
    const target = 0.35;
    const startedAt = Date.now();
    const durMs = 2000;
    let raf;
    const tick = () => {
      const t = Math.min(1, (Date.now() - startedAt) / durMs);
      // Ease-out cubic — softer landing at target volume.
      const eased = 1 - Math.pow(1 - t, 3);
      if (audioRef.current) audioRef.current.volume = eased * target;
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    // Wire the audio graph *before* .play() so the first frame of
    // audio already flows through the analyser (avoids a visible
    // 100ms silence-strip on first tune-in).
    ensureAudioGraph();
    el.play().then(() => {
      setAudioPlaying(true);
      raf = requestAnimationFrame(tick);
      startWaveformLoop();
    }).catch(() => {
      // Autoplay blocked — leave volume at target-ish so the manual
      // play button starts at a sensible level.
      if (audioRef.current) audioRef.current.volume = target;
      setAudioPlaying(false);
    });
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [activeAudioSrc, isArchivedView]);

  // ESC closes the view. Keep the listener scoped to this mount so it
  // doesn't shadow the guide/browse ESC handlers. Nested view modes
  // (list / archived) pop back to the previous surface before ESC
  // finally closes the whole overlay — mimics the natural "back stack"
  // a viewer expects.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (lightbox) { setLightbox(null); return; }
      if (viewMode === "archived") {
        setArchived(null);
        setViewMode("list");
        return;
      }
      if (viewMode === "list") {
        setViewMode("current");
        return;
      }
      onClose && onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, lightbox, viewMode]);

  const linkedChannel = useMemo(() => {
    if (!active?.linked_channel_id) return null;
    return (channels || []).find((c) => c.id === active.linked_channel_id) || null;
  }, [channels, active?.linked_channel_id]);
  const openArchived = async (id) => {
    try {
      const r = await axios.get(`${API}/site/tribute/archive/${id}`);
      setArchived(r.data);
      setViewMode("archived");
    } catch (e) {
      // Silent fail — the picker stays open, viewer can pick another.
    }
  };

  if (!tribute || !tribute.enabled) {
    // Defensive — the view should only mount when a tribute is published,
    // but if something races into rendering an empty state, we surface a
    // dignified placeholder instead of a blank screen.
    return (
      <div className="fixed inset-0 z-[80] bg-black text-neutral-300 flex flex-col items-center justify-center" data-testid="tribute-view-empty">
        <Heart size={28} className="opacity-40 mb-3" />
        <div className="text-sm tracking-[0.35em] opacity-70">NO CURRENT TRIBUTE</div>
        <button
          type="button"
          onClick={onClose}
          className="mt-6 text-[11px] tracking-[0.28em] border border-neutral-700 px-3 py-1.5 hover:border-neutral-400 transition-colors"
        >
          ◄ CLOSE
        </button>
      </div>
    );
  }

  // ── ARCHIVE PICKER ────────────────────────────────────────────────
  // Compact grid of past tributes — thumbnail + name + years. Same
  // sepia treatment as the current view so the aesthetic stays
  // consistent when the viewer flips between them.
  if (viewMode === "list") {
    return (
      <div
        className="fixed inset-0 z-[80] overflow-y-auto bg-black"
        style={{
          // Fully opaque black base + a subtle sepia radial as an
          // *overlay* rather than a semi-transparent gradient. Every
          // colour stop here is fully opaque so the CRT tube behind
          // never bleeds through — the earlier rgba(120,80,40,0.18)
          // top stop was letting the tv show, making the tribute
          // page feel see-through.
          background:
            "radial-gradient(ellipse at 50% 0%, #251810 0%, #0a0603 45%, #000 100%)",
          fontFamily: "VT323, monospace",
        }}
        data-testid="tribute-archive-list"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-3 bg-gradient-to-b from-black via-black/85 to-transparent">
          <button
            type="button"
            onClick={() => setViewMode("current")}
            data-testid="tribute-archive-back-current"
            className="flex items-center gap-1.5 text-neutral-300 hover:text-neutral-100"
          >
            <ChevronLeft size={14} />
            <span className="text-[11px] tracking-[0.32em]">BACK TO CURRENT</span>
          </button>
          <div className="text-[11px] tracking-[0.4em] text-neutral-500">◈ PAST TRIBUTES ◈</div>
          <button
            type="button"
            onClick={onClose}
            data-testid="tribute-archive-close"
            className="text-[11px] tracking-[0.28em] text-neutral-400 hover:text-neutral-100 border border-neutral-700 hover:border-neutral-400 px-3 py-1"
          >
            ESC · CLOSE
          </button>
        </div>
        <div className="max-w-5xl mx-auto px-6 pb-16 pt-4">
          {archives.length === 0 ? (
            <div className="text-center text-neutral-500 text-[13px] tracking-[0.15em] py-20" data-testid="tribute-archive-empty">
              No past tributes yet.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {archives.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => openArchived(a.id)}
                  data-testid={`tribute-archive-card-${a.id}`}
                  className="group text-left border border-neutral-800 bg-neutral-950/70 hover:border-neutral-500 hover:bg-neutral-900 transition-colors"
                >
                  <div className="aspect-[16/9] bg-neutral-950 overflow-hidden">
                    {a.hero_url ? (
                      <img
                        src={a.hero_url}
                        alt={a.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                        style={{ filter: "sepia(0.35) contrast(0.95) brightness(0.92)" }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-700">
                        <Heart size={22} />
                      </div>
                    )}
                  </div>
                  <div className="px-3 py-2">
                    <div className="text-neutral-100 text-[16px] tracking-[0.04em] truncate">{a.name}</div>
                    <div className="text-[11px] tracking-[0.24em] text-neutral-500 mt-0.5">
                      {a.birth_year || "?"} — {a.death_year || "?"}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (audioPlaying) {
      el.pause();
      setAudioPlaying(false);
      stopWaveformLoop();
    } else {
      ensureAudioGraph();
      el.play().then(() => {
        setAudioPlaying(true);
        startWaveformLoop();
      }).catch(() => {});
    }
  };
  const toggleMute = () => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setAudioMuted(el.muted);
  };

  return (
    <div
      className="fixed inset-0 z-[80] overflow-y-auto bg-black"
      style={{
        // Fully opaque background so the CRT tube behind the overlay
        // doesn't bleed through — the earlier rgba(...,0.98) top stop
        // was letting the tv peek through and making the tribute page
        // feel semi-transparent. Every colour stop is now solid.
        background:
          "radial-gradient(ellipse at 50% 0%, #2a1a10 0%, #0a0603 45%, #000 100%)",
        fontFamily: "VT323, monospace",
      }}
      data-testid="tribute-view"
    >
      {/* Top close bar — quiet, no phosphor glow */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-3 bg-gradient-to-b from-black via-black/85 to-transparent">
        <div className="flex items-center gap-2 text-neutral-300">
          <Heart size={14} className="opacity-70" />
          <span className="text-[11px] tracking-[0.4em] opacity-80">
            {isArchivedView ? "IN MEMORIAM · ARCHIVE" : "IN MEMORIAM"}
          </span>
        </div>
        {isArchivedView ? (
          <button
            type="button"
            onClick={() => { setArchived(null); setViewMode("list"); }}
            data-testid="tribute-back-to-list"
            className="text-[11px] tracking-[0.28em] text-neutral-300 hover:text-neutral-100 border border-neutral-700 hover:border-neutral-400 px-3 py-1 flex items-center gap-1"
          >
            <ChevronLeft size={12} /> PAST TRIBUTES
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            data-testid="tribute-view-close"
            className="text-[11px] tracking-[0.28em] text-neutral-400 hover:text-neutral-100 border border-neutral-700 hover:border-neutral-400 px-3 py-1"
          >
            ESC · CLOSE
          </button>
        )}
      </div>

      <div className="max-w-3xl mx-auto px-6 pb-16">
        {/* Hero image — sepia-tinted portrait (399×501 fixed size,
            centered). Portrait aspect (~4:5) suits headshots and
            memorial photography better than the earlier full-width
            16:9 landscape. object-cover still fills, cropping
            gracefully if the source doesn't match. */}
        {active.hero_url && (
          <div
            className="mt-4 mb-6 mx-auto relative bg-neutral-950 border border-neutral-800 overflow-hidden"
            style={{ width: 399, height: 501, maxWidth: "100%" }}
          >
            <img
              src={active.hero_url}
              alt={active.name}
              className="w-full h-full object-cover"
              style={{ filter: "sepia(0.35) contrast(0.95) brightness(0.92)" }}
              data-testid="tribute-hero"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent 55%, rgba(0,0,0,0.65) 100%)" }} />
          </div>
        )}

        {/* Name + years */}
        <div className="text-center mb-6">
          <h1
            className="text-4xl sm:text-5xl tracking-[0.05em] text-neutral-100 font-light"
            data-testid="tribute-name-display"
          >
            {active.name}
          </h1>
          {(active.birth_year || active.death_year) && (
            <div className="mt-2 text-[13px] tracking-[0.32em] text-neutral-400" data-testid="tribute-years-display">
              {active.birth_year || "?"} — {active.death_year || "?"}
            </div>
          )}
        </div>

        {/* Audio bar — muted controls; auto-fade fires on mount for the
            current tribute only. Archived views get the same controls
            but the audio starts paused so it never startles.

            Below the play/pause row: a subtle 64-bar waveform strip
            driven by Web Audio AnalyserNode. Neutral-500 → 205 grey
            gradient by amplitude, sine-window taper, 24px tall. Pauses
            when the audio pauses; last frame stays painted so the
            strip doesn't blink out. */}
        {activeAudioSrc && (
          <div
            className="border border-neutral-800 bg-black/50 mb-6"
            data-testid="tribute-audio-bar"
          >
            <div className="flex items-center gap-3 px-3 py-2">
              <button
                type="button"
                onClick={togglePlay}
                className="text-neutral-200 hover:text-white"
                data-testid="tribute-audio-toggle"
                title={audioPlaying ? "Pause" : "Play"}
              >
                {audioPlaying ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <div className="flex-1 text-[11px] tracking-[0.24em] text-neutral-400">
                {audioPlaying ? "▶ IN TRIBUTE" : "◼ PAUSED"}
              </div>
              <button
                type="button"
                onClick={toggleMute}
                className="text-neutral-400 hover:text-neutral-100"
                data-testid="tribute-audio-mute"
                title={audioMuted ? "Unmute" : "Mute"}
              >
                {audioMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
              </button>
              <audio
                ref={audioRef}
                src={activeAudioSrc}
                loop
                preload="auto"
                crossOrigin="anonymous"
                data-testid="tribute-audio-el"
              />
            </div>
            <canvas
              ref={canvasRef}
              className="block w-full"
              style={{ height: 24, opacity: audioPlaying ? 0.9 : 0.35, transition: "opacity 400ms ease-out" }}
              data-testid="tribute-audio-waveform"
              aria-hidden="true"
            />
          </div>
        )}

        {/* Obituary — free-form text, whitespace preserved */}
        {active.obituary && (
          <div
            className="text-neutral-200 leading-relaxed whitespace-pre-wrap text-[17px] tracking-[0.02em] mb-8"
            data-testid="tribute-obituary-display"
            style={{ fontFamily: "VT323, monospace" }}
          >
            {active.obituary}
          </div>
        )}

        {/* Gallery — click to enlarge */}
        {active.gallery && active.gallery.length > 0 && (
          <div className="mb-8" data-testid="tribute-gallery">
            <div className="text-[11px] tracking-[0.32em] text-neutral-500 mb-2">◢ GALLERY ◣</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {active.gallery.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setLightbox(g)}
                  data-testid={`tribute-gallery-open-${g.id}`}
                  className="group relative aspect-square bg-neutral-950 border border-neutral-800 overflow-hidden hover:border-neutral-500 transition-colors"
                  title={g.caption || ""}
                >
                  <img
                    src={g.url}
                    alt={g.caption || ""}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                    style={{ filter: "sepia(0.25) contrast(0.98)" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  {g.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-[10px] tracking-widest text-neutral-200 px-1.5 py-0.5 truncate">
                      {g.caption}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Linked channel / tape */}
        {linkedChannel && (
          <div className="border border-neutral-800 bg-neutral-950/70 p-3 mb-8" data-testid="tribute-linked-block">
            <div className="text-[11px] tracking-[0.32em] text-neutral-500 mb-1.5">◢ WATCH THEIR WORK ◣</div>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-neutral-100 text-[15px] tracking-[0.06em] truncate">
                  CH {String(linkedChannel.number).padStart(2, "0")} — {linkedChannel.name}
                </div>
                {active.linked_video_id && (
                  <div className="text-[11px] tracking-[0.15em] text-neutral-500 mt-0.5 truncate">
                    tape · {active.linked_video_id.slice(0, 8)}…
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (active.linked_video_id && onOpenTape) {
                    onOpenTape(linkedChannel.id, active.linked_video_id);
                  } else if (onTuneChannel) {
                    onTuneChannel(linkedChannel.id);
                  }
                  onClose && onClose();
                }}
                data-testid="tribute-tune-to-linked"
                className="flex items-center gap-1.5 text-[11px] tracking-[0.24em] text-neutral-100 border border-neutral-500 px-3 py-1.5 hover:bg-neutral-100 hover:text-black transition-colors"
              >
                <ExternalLink size={12} />
                TUNE IN
              </button>
            </div>
          </div>
        )}

        {/* Past-tributes link — only shown on the CURRENT view. On an
            archived view we already have the "◄ PAST TRIBUTES" button
            in the top bar and don't want a competing footer link. */}
        {!isArchivedView && archives.length > 0 && (
          <div className="mb-6 flex justify-center" data-testid="tribute-open-archive-row">
            <button
              type="button"
              onClick={() => setViewMode("list")}
              data-testid="tribute-open-archive"
              className="flex items-center gap-2 text-neutral-400 hover:text-neutral-100 text-[12px] tracking-[0.32em] border border-neutral-800 hover:border-neutral-500 px-4 py-2 transition-colors"
              title="Browse the past tributes archive"
            >
              <Archive size={12} />
              ◄ PAST TRIBUTES ({archives.length})
            </button>
          </div>
        )}

        {/* Footer sig */}
        <div className="text-center text-[10px] tracking-[0.35em] text-neutral-600 mt-10">
          ◈ REST WELL ◈
        </div>
      </div>

      {/* Lightbox — click backdrop to close */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[85] bg-black/95 flex flex-col items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="tribute-lightbox"
        >
          <img
            src={lightbox.url}
            alt={lightbox.caption || ""}
            className="max-w-full max-h-[80vh] object-contain border border-neutral-800"
            style={{ filter: "sepia(0.2)" }}
          />
          {lightbox.caption && (
            <div className="mt-4 text-neutral-300 text-[13px] tracking-[0.1em] text-center max-w-2xl">
              {lightbox.caption}
            </div>
          )}
          <div className="mt-4 text-[10px] tracking-[0.32em] text-neutral-600">CLICK ANYWHERE TO CLOSE</div>
        </div>
      )}
    </div>
  );
}
